import React, { useEffect } from "react";
import { api } from "../api";

// In-app calling (Salem, 2026-07-23) — two layers that degrade gracefully:
//
// 1. RingCentral Embeddable widget (the full dialer docked in RepIQ). Loaded once per session
//    when RINGCENTRAL_EMBED_CLIENT_ID is configured (Settings → Railway env). Each rep signs
//    into RingCentral inside the widget once; calls then happen right in the browser, and our
//    normal ingestion (webhook/poller) analyses them like any other call.
// 2. Click-to-dial fallback. When the widget isn't loaded, any <DialLink> falls back to a
//    tel: link — Windows hands the number to the RingCentral desktop app with it pre-filled.
//
// Use <DialLink number={...}> anywhere a phone number is shown. It prefers the widget
// (postMessage rc-adapter-new-call) and falls back automatically.

let _initStarted = false;

export async function initRcWidget() {
  if (_initStarted || document.getElementById("rc-embeddable-adapter")) return;
  _initStarted = true;
  let cfg = null;
  try {
    cfg = await api.get("/api/auth/phone-config");
  } catch {
    return;                                   // not signed in / offline — tel: fallback stands
  }
  if (!cfg?.rcEmbedClientId) return;          // widget not configured — tel: fallback stands
  const params = new URLSearchParams({
    clientId: cfg.rcEmbedClientId,
    appServer: cfg.rcServer || "https://platform.ringcentral.com",
  });
  const s = document.createElement("script");
  s.id = "rc-embeddable-adapter";
  s.src = `https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js?${params.toString()}`;
  s.async = true;
  document.body.appendChild(s);
  installContactsBridge();
}

// RepIQ as a contact source inside the dialer (Salem 2026-10-08: the widget's Contacts tab
// shows only the account's extensions, not the shared external directory). The widget asks
// for /contacts (paged), /contacts/search and /contacts/match; we answer from
// /api/phonebook/dialer/*: the admin phone book (BT Billing, BT Faults…) and CompanyIQ.
// Registration is re-sent on every widget "ready"/login notice — it is idempotent.
let _bridgeInstalled = false;
let _registered = false;
const SERVICE = "RepIQ";
const register = () => {
  const frame = widgetFrame();
  if (!frame || !frame.contentWindow) return;
  frame.contentWindow.postMessage({
    type: "rc-adapter-register-third-party-service",
    service: {
      name: SERVICE,
      contactsPath: "/contacts",
      contactSearchPath: "/contacts/search",
      contactMatchPath: "/contacts/match",
    },
  }, "*");
};
const respond = (requestId, response) => {
  const frame = widgetFrame();
  if (!frame || !frame.contentWindow) return;
  frame.contentWindow.postMessage({ type: "rc-post-message-response", responseId: requestId, response }, "*");
};
function installContactsBridge() {
  if (_bridgeInstalled) return;
  _bridgeInstalled = true;
  window.addEventListener("message", async (e) => {
    const d = e.data;
    if (!d || typeof d !== "object") return;
    if (d.type === "rc-login-status-notify") {
      if (d.loggedIn && !_registered) { register(); _registered = true; }
      if (!d.loggedIn) _registered = false;
      return;
    }
    if (d.type === "rc-adapter-pushAdapterState" && !_registered) {
      register(); _registered = true;          // widget ready before any login notice
      return;
    }
    if (d.type !== "rc-post-message-request") return;
    try {
      if (d.path === "/contacts") {
        const page = Number(d.body?.page || 1);
        const r = await api.get(`/api/phonebook/dialer/contacts?page=${page}`);
        respond(d.requestId, { data: r.data || [], nextPage: r.nextPage ?? null, syncTimestamp: r.syncTimestamp ?? Date.now() });
      } else if (d.path === "/contacts/search") {
        const q = String(d.body?.searchString || "");
        const r = q.trim() ? await api.get(`/api/phonebook/dialer/search?q=${encodeURIComponent(q)}`) : { data: [] };
        respond(d.requestId, { data: r.data || [] });
      } else if (d.path === "/contacts/match") {
        const nums = Array.isArray(d.body?.phoneNumbers) ? d.body.phoneNumbers : [];
        const r = nums.length ? await api.post("/api/phonebook/dialer/match", { phoneNumbers: nums }) : { data: {} };
        respond(d.requestId, { data: r.data || {} });
      }
    } catch (err) {
      // Never leave the widget waiting: an empty answer is better than a hung request.
      if (d.path === "/contacts") respond(d.requestId, { data: [], nextPage: null, syncTimestamp: Date.now() });
      else respond(d.requestId, { data: d.path === "/contacts/match" ? {} : [] });
    }
  });
}

const widgetFrame = () => document.querySelector("#rc-widget-adapter-frame");

export function dialNumber(number) {
  const n = String(number || "").trim();
  if (!n) return;
  // Tell Live Assist a call is about to start — it shows the brief while the phone rings,
  // whichever dialer answers below.
  try { window.dispatchEvent(new CustomEvent("repiq:dial", { detail: { number: n } })); } catch { /* old browser */ }
  const frame = widgetFrame();
  if (frame && frame.contentWindow) {
    // Pop the widget open and start the call in it.
    frame.contentWindow.postMessage({ type: "rc-adapter-syncMinimized", minimized: false }, "*");
    frame.contentWindow.postMessage({ type: "rc-adapter-new-call", phoneNumber: n, toCall: true }, "*");
  } else {
    // No widget — hand the number to the RingCentral desktop app via the OS tel: handler.
    window.location.href = `tel:${n.replace(/[\s()-]/g, "")}`;
  }
}

// Mounted once in the signed-in app shell — loads the widget if it's configured.
export function RcWidgetLoader() {
  useEffect(() => { initRcWidget(); }, []);
  return null;
}

// A phone number you can click to call. Renders children (or "📞 {number}") as a link.
export default function DialLink({ number, className, style, title, children }) {
  if (!number) return null;
  return (
    <a href={`tel:${String(number).replace(/[\s()-]/g, "")}`}
       className={className} style={{ cursor: "pointer", ...style }}
       title={title || "Call with RingCentral"}
       onClick={(e) => { e.preventDefault(); e.stopPropagation(); dialNumber(number); }}>
      {children ?? <>📞 {number}</>}
    </a>
  );
}
